import { useState, useRef } from 'react';
import PhotoPickerModal from '../PhotoPickerModal';
import { getAutoAdvisorAnalyses } from '../../utils/storage';
import { compressImageFile } from '../../utils/imageUtils';

export default function RepairQuoteInput({ data, update, onNext, onBack, onViewSaved }) {
  const [tab, setTab] = useState('manual'); // 'photo' | 'manual'
  const [lineItems, setLineItems] = useState(data.repairLineItems || [{ description: '', cost: '' }]);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);
  const fileRef = useRef(null);

  // Compute once per render — saved reports list is small and reading
  // localStorage is cheap. Used to gate the "View saved reports" button.
  const savedCount = getAutoAdvisorAnalyses().length;

  function updateLineItem(index, field, value) {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    setLineItems(updated);
    update({ repairLineItems: updated });
  }

  function addLineItem() {
    const updated = [...lineItems, { description: '', cost: '' }];
    setLineItems(updated);
    update({ repairLineItems: updated });
  }

  function removeLineItem(index) {
    if (lineItems.length <= 1) return;
    const updated = lineItems.filter((_, i) => i !== index);
    setLineItems(updated);
    update({ repairLineItems: updated });
  }

  function handlePhotoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    compressImageFile(file, 1200, 0.8).then((dataUrl) => update({ repairQuotePhoto: dataUrl })).catch(() => {});
    e.target.value = '';
  }

  const canProceed =
    tab === 'photo'
      ? !!data.repairQuotePhoto
      : !!(data.repairDescription && data.repairTotalQuoted);

  return (
    <>
      <div className="screen-header">
        <h1 className="screen-title">Repair Quote Details</h1>
        <p className="screen-subtitle">
          Upload a photo of your repair quote or enter the details manually.
        </p>
      </div>

      {/* Tab switcher */}
      <div style={{
        display: 'flex',
        gap: 0,
        borderRadius: 100,
        overflow: 'hidden',
        border: '1.5px solid var(--border)',
        marginBottom: 20,
      }}>
        {['photo', 'manual'].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              flex: 1,
              padding: '10px 0',
              fontSize: '0.8125rem',
              fontWeight: 600,
              background: tab === t ? 'var(--accent)' : 'transparent',
              color: tab === t ? 'white' : 'var(--text-muted)',
              transition: 'all 0.15s',
              textTransform: 'capitalize',
            }}
          >
            {t === 'photo' ? 'Upload Photo' : 'Enter Manually'}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, paddingTop: 8 }}>
        {tab === 'photo' && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handlePhotoUpload}
            />
            {data.repairQuotePhoto ? (
              <button
                type="button"
                onClick={() => setShowPhotoPicker(true)}
                style={{
                  width: '100%',
                  height: 200,
                  borderRadius: 'var(--radius-sm)',
                  background: `url(${data.repairQuotePhoto}) center/contain no-repeat`,
                  backgroundColor: 'var(--bg-elevated)',
                  border: '1.5px solid var(--border)',
                  cursor: 'pointer',
                  position: 'relative',
                  marginBottom: 12,
                }}
              >
                <div style={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: 'rgba(0,0,0,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                </div>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowPhotoPicker(true)}
                style={{
                  width: '100%',
                  height: 160,
                  borderRadius: 'var(--radius-sm)',
                  border: '2px dashed var(--border)',
                  background: 'var(--bg-input)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  marginBottom: 12,
                }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                <span style={{ fontSize: '0.875rem' }}>Tap to upload repair quote photo</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  We&apos;ll scan the document and extract the details
                </span>
              </button>
            )}
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center' }}>
              Take a clear photo of the repair estimate or invoice
            </p>
          </>
        )}

        {tab === 'manual' && (
          <>
            <div className="form-group">
              <label className="form-label">Shop Name (Optional)</label>
              <input
                className="form-input"
                type="text"
                placeholder="e.g. Joe's Auto Repair"
                value={data.repairShopName || ''}
                onChange={(e) => update({ repairShopName: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Repair Description</label>
              <textarea
                className="form-input"
                placeholder="e.g. Replace timing belt, water pump, and tensioner"
                value={data.repairDescription || ''}
                onChange={(e) => update({ repairDescription: e.target.value })}
                rows={3}
                style={{ resize: 'vertical', minHeight: 72 }}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Total Quoted</label>
              <input
                className="form-input"
                type="text"
                inputMode="decimal"
                placeholder="e.g. $1,250"
                value={data.repairTotalQuoted || ''}
                onChange={(e) => update({ repairTotalQuoted: e.target.value })}
              />
            </div>

            {/* Line items */}
            <div style={{ marginBottom: 16 }}>
              <label className="form-label" style={{ marginBottom: 10 }}>
                Line Items (Optional)
              </label>
              {lineItems.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="Item description"
                    value={item.description}
                    onChange={(e) => updateLineItem(i, 'description', e.target.value)}
                    style={{ flex: 2 }}
                  />
                  <input
                    className="form-input"
                    type="text"
                    inputMode="decimal"
                    placeholder="$"
                    value={item.cost}
                    onChange={(e) => updateLineItem(i, 'cost', e.target.value)}
                    style={{ flex: 1 }}
                  />
                  {lineItems.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLineItem(i)}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: 'var(--bg-elevated)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 'none',
                        cursor: 'pointer',
                        flexShrink: 0,
                        color: 'var(--danger)',
                        fontSize: '1rem',
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addLineItem}
                style={{
                  fontSize: '0.8125rem',
                  color: 'var(--accent)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  padding: '4px 0',
                }}
              >
                + Add Line Item
              </button>
            </div>
          </>
        )}
      </div>

      {/* "View saved reports" jumps back to the mode selector where the
          saved reports list lives. Only show it when there's actually
          history to view. */}
      {savedCount > 0 && onViewSaved && (
        <button
          type="button"
          onClick={onViewSaved}
          style={{
            width: '100%',
            padding: '10px 0',
            marginTop: 12,
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: 'var(--accent)',
            background: 'transparent',
            border: '1px solid var(--accent)',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
          }}
        >
          View saved reports ({savedCount})
        </button>
      )}

      <div className="nav-row">
        <button className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        <button className="btn btn-primary" disabled={!canProceed} onClick={onNext}>
          Continue
        </button>
      </div>

      <PhotoPickerModal
        show={showPhotoPicker}
        onClose={() => setShowPhotoPicker(false)}
        onSelect={(source) => {
          setShowPhotoPicker(false);
          fileRef.current.removeAttribute('capture');
          fileRef.current.click();
        }}
      />
    </>
  );
}
